import type { IDictionariesMap, IDictionary } from '@sravni/types/lib/dictionaries';
import type { IVitrinaConfig } from '@sravni/vitrina/lib/types/config/IVitrinaConfig';
import type IProduct from '@sravni/vitrina/lib/types/IProduct';

import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { getDictionaries, getProductConfig } from '../../services/productPopupInfo';
import type { GlobalState } from '../index';
import { getDictionariesSelector } from '../selectors/productPopupInfo';

import { fetchRatingsWithProductInfo } from './ratings';

export type FetchConfigParams = {
  serviceKey: string;
};

export const fetchProductConfig = createAsyncThunk<IVitrinaConfig, FetchConfigParams>(
  'productPopup/fetchConfig',
  async (params) => getProductConfig(params),
);

export const fetchDictionaries = createAsyncThunk<
  IDictionariesMap,
  undefined,
  {
    state: GlobalState;
  }
>('productPopup/Dictionaries', async (_, { getState }) => {
  const dictionaries = getDictionariesSelector(getState());
  if (dictionaries) return dictionaries;

  return getDictionaries();
});

export interface IState {
  productConfig: IVitrinaConfig;
  dictionaries: IDictionariesMap;
  products: Record<string, IProduct>;
}

const initialState: IState = {
  productConfig: null,
  dictionaries: null,
  products: null,
};

const productPopupInfoSlice = createSlice({
  name: 'productPopupInfo',
  initialState,
  reducers: {
    setProductConfig: (state, { payload }: PayloadAction<IVitrinaConfig>) => {
      state.productConfig = payload;
    },
    setDictionaries: (state, { payload }: PayloadAction<Record<string, IDictionary>>) => {
      state.dictionaries = payload;
    },
    setProducts: (state, { payload }: PayloadAction<Record<string, IProduct>>) => {
      state.products = payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchRatingsWithProductInfo.fulfilled, (state, { payload }) => {
      state.products = payload.productInfoById;
    });
    builder.addCase(fetchProductConfig.fulfilled, (state, { payload }) => {
      state.productConfig = payload;
    });
    builder.addCase(fetchDictionaries.fulfilled, (state, { payload }) => {
      state.dictionaries = payload;
    });
  },
});

const { actions, reducer } = productPopupInfoSlice;

export const { setProductConfig, setProducts, setDictionaries } = actions;

export default reducer;
